import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useApiQuery } from "@/lib/useApiQuery";
import {
  Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { ArrowLeft, Snowflake, Flame, Droplets, Wind, Waves, Gauge, Cog, AlertOctagon, Zap } from "lucide-react";
import { AXIS, LABEL_STYLE, SMALL_CHART_MARGIN, TOOLTIP_STYLE } from "@/lib/chartStyles";

const SYSTEM_META = {
  "Chiller plant": { Icon: Snowflake, color: "#0E7490" },
  Boilers: { Icon: Flame, color: "#C2410C" },
  "Domestic hot water": { Icon: Droplets, color: "#0369A1" },
  "Air handling": { Icon: Wind, color: "#156F5B" },
  Pumps: { Icon: Waves, color: "#7C3AED" },
  Metering: { Icon: Gauge, color: "#B45309" },
  Other: { Icon: Cog, color: "#6B7280" },
};

const RANGES = [
  { label: "24h", days: 1 },
  { label: "7d", days: 7 },
];

function PointValue({ p }) {
  const val = p.cur_val;
  const shown = typeof val === "number" ? val.toLocaleString(undefined, { maximumFractionDigits: 2 }) : val ?? "—";
  const fault = p.cur_status === "fault";
  return (
    <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-b border-[#F0F2F1] last:border-0" data-testid={`equip-point-${p.point_id}`}>
      <div className="min-w-0">
        <div className="text-sm text-[#212121] truncate">{p.dis}</div>
        <div className="text-[10px] uppercase tracking-wider text-[#9CA3AF] mt-0.5">
          {p.sp ? "Setpoint" : p.writable ? "Command" : "Sensor"}
        </div>
      </div>
      <div className={`text-sm font-heading font-semibold shrink-0 ${fault ? "text-[#B91C1C]" : "text-[#212121]"}`}>
        {shown}
        {p.unit ? <span className="ml-1 text-xs text-[#6B7280]">{p.unit}</span> : null}
      </div>
    </div>
  );
}

function TrendCard({ s }) {
  const data = useMemo(
    () =>
      (s.series || []).map((r) => ({
        label: new Date(r.ts).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric" }),
        val: typeof r.val === "object" ? r.val?.val : r.val,
      })),
    [s]
  );
  return (
    <div className="k-card p-5" data-testid={`equip-chart-${s.point_id}`}>
      <div className="flex items-center justify-between">
        <div className="text-sm font-heading font-semibold text-[#212121] truncate">{s.dis}</div>
        <span className="k-sublabel">{s.unit || ""}</span>
      </div>
      <div className="mt-3 h-40">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={SMALL_CHART_MARGIN}>
            <defs>
              <linearGradient id={`eg-${s.point_id}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#156F5B" stopOpacity={0.35} />
                <stop offset="100%" stopColor="#156F5B" stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="#F0F2F1" vertical={false} />
            <XAxis dataKey="label" tick={AXIS} interval="preserveStartEnd" minTickGap={60} />
            <YAxis tick={AXIS} width={46} />
            <Tooltip contentStyle={TOOLTIP_STYLE} labelStyle={LABEL_STYLE} />
            <Area type="monotone" dataKey="val" stroke="#156F5B" strokeWidth={1.5} fill={`url(#eg-${s.point_id})`} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function OpenFaults({ detail }) {
  if (!detail.faults?.length) {
    return (
      <div className="mt-6 k-card p-5 text-sm text-[#6B7280]" data-testid="equipment-no-faults">
        No open faults on this equipment this week.
      </div>
    );
  }
  return (
    <div className="mt-6" data-testid="equipment-faults">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="k-label flex items-center gap-2">
          <AlertOctagon className="h-3.5 w-3.5 text-[#B91C1C]" /> Open faults this week
        </h3>
        <div className="text-xs text-[#6B7280]">
          {detail.fault_count} fault{detail.fault_count === 1 ? "" : "s"} · {detail.fault_hours.toFixed(1)} hr
          {detail.fault_cost ? ` · $${detail.fault_cost.toLocaleString(undefined, { maximumFractionDigits: 0 })}` : ""}
        </div>
      </div>
      <div className="mt-2 k-card overflow-hidden">
        {detail.faults.map((f, i) => (
          <div key={f.rule_id} className="px-4 py-3 border-b border-[#F0F2F1] last:border-0" data-testid={`equipment-fault-${i}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <div className="text-sm font-heading font-semibold text-[#212121] flex items-center gap-2">
                  {f.critical && <AlertOctagon className="h-3.5 w-3.5 text-[#B91C1C] shrink-0" />}
                  <span className="truncate">{f.rule}</span>
                </div>
                {f.help && <div className="text-[11px] text-[#6B7280] mt-1 line-clamp-2 max-w-xl">{f.help}</div>}
              </div>
              <div className="text-right shrink-0">
                <div className="text-sm font-heading font-semibold text-[#212121]">{f.hours.toFixed(1)} hr</div>
                {f.cost ? (
                  <div className="text-[11px] text-[#C2410C]">
                    ${f.cost.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  </div>
                ) : null}
              </div>
            </div>
            {f.days?.length ? (
              <div className="mt-2 flex gap-1 items-end h-6">
                {f.days.map((h, di) => {
                  const peak = Math.max(...f.days, 0.0001);
                  return (
                    <div key={`${f.rule || f.dis}-day-${di}`} className="flex-1 bg-[#F3F4F6] rounded-sm flex items-end h-full">
                      <div
                        className="w-full rounded-sm"
                        style={{ height: `${(h / peak) * 100}%`, minHeight: h > 0 ? 3 : 0, background: f.critical ? "#B91C1C" : "#C2410C" }}
                      />
                    </div>
                  );
                })}
              </div>
            ) : null}
          </div>
        ))}
      </div>
      <Link to="/faults" className="mt-3 inline-flex items-center gap-1 text-[11px] font-heading font-semibold text-[#156F5B] hover:underline" data-testid="equipment-faults-link">
        Open the Faults page for full history
      </Link>
    </div>
  );
}

export default function EquipmentPage() {
  const { siteId } = useParams();
  const [selected, setSelected] = useState(null);
  const [days, setDays] = useState(1);

  const { data: equipData, loading, err: equipErr } = useApiQuery(
    ["equipment", siteId], `/sites/${encodeURIComponent(siteId)}/equipment`
  );
  const { data: site } = useApiQuery(["site", siteId], `/sites/${encodeURIComponent(siteId)}`);
  const equips = useMemo(() => equipData || [], [equipData]);

  useEffect(() => {
    if (equips.length && !equips.some((e) => e.equip_id === selected)) setSelected(equips[0].equip_id);
  }, [equips, selected]);

  // Fast pass: live values + open faults. Charts follow in a second call.
  const { data: fast, loading: loadingDetail, err: fastErr } = useApiQuery(
    ["equipDetail", selected, days, "fast"], `/equipment/${encodeURIComponent(selected || "")}`,
    { params: { days, charts: false }, enabled: Boolean(selected) }
  );
  const { data: full, loading: loadingCharts } = useApiQuery(
    ["equipDetail", selected, days, "full"], `/equipment/${encodeURIComponent(selected || "")}`,
    { params: { days }, enabled: Boolean(selected && fast) }
  );
  const detail = full || fast;
  const err = equipErr || fastErr;

  const groups = useMemo(() => {
    const map = new Map();
    equips.forEach((e) => {
      if (!map.has(e.system)) map.set(e.system, []);
      map.get(e.system).push(e);
    });
    return Array.from(map.entries());
  }, [equips]);

  return (
    <div className="max-w-7xl fade-up">
      <Link to={`/sites/${encodeURIComponent(siteId)}`} className="k-sublabel inline-flex items-center gap-1.5 hover:text-[#156F5B]" data-testid="equipment-back">
        <ArrowLeft className="h-3 w-3" /> Back to site
      </Link>

      <div className="mt-4 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="k-label">Systems</div>
          <h1 className="mt-2 text-4xl sm:text-5xl font-bold font-display text-[#212121]" data-testid="equipment-title">
            {site?.dis || "Equipment"}
          </h1>
          <p className="mt-2 text-sm text-[#6B7280]">
            {loading ? "Loading equipment…" : `${equips.length} pieces of equipment across ${groups.length} systems`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link to={`/sites/${encodeURIComponent(siteId)}/energy`} className="k-btn-outline" data-testid="equipment-energy-link">
            <Zap className="h-3 w-3" /> Energy &amp; cost
          </Link>
          <div className="flex rounded-md border border-[#E5E7EB] overflow-hidden">
            {RANGES.map((r) => (
              <button
                key={r.days}
                onClick={() => setDays(r.days)}
                data-testid={`equipment-range-${r.label}`}
                className={`px-3 py-1.5 text-xs font-heading font-semibold ${
                  days === r.days ? "bg-[#156F5B] text-white" : "bg-white text-[#4D4D4D] hover:bg-[#F3F8F7]"
                }`}
                style={{ transition: "background-color .2s ease, color .2s ease" }}
              >
                {r.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {err && <div className="mt-6 k-card p-5 text-sm text-red-600" data-testid="equipment-error">{err}</div>}

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Equipment list */}
        <div className="lg:col-span-4 space-y-6" data-testid="equipment-list">
          {groups.map(([system, list]) => {
            const meta = SYSTEM_META[system] || SYSTEM_META.Other;
            return (
              <div key={system}>
                <div className="flex items-center gap-2">
                  <meta.Icon className="h-3.5 w-3.5" style={{ color: meta.color }} />
                  <h2 className="k-label">{system}</h2>
                  <span className="text-[11px] text-[#9CA3AF]">{list.length}</span>
                </div>
                <div className="mt-2 k-card overflow-hidden">
                  {list.map((e) => (
                    <button
                      key={e.equip_id}
                      onClick={() => setSelected(e.equip_id)}
                      data-testid={`equipment-item-${e.equip_id}`}
                      className={`w-full text-left px-4 py-3 border-b border-[#F0F2F1] last:border-0 flex items-center justify-between gap-3 ${
                        selected === e.equip_id ? "bg-[#F3F8F7]" : "hover:bg-[#F9FAFB]"
                      }`}
                      style={{ transition: "background-color .2s ease" }}
                    >
                      <div className="min-w-0">
                        <div className={`text-sm font-heading font-semibold truncate ${selected === e.equip_id ? "text-[#156F5B]" : "text-[#212121]"}`}>
                          {e.dis}
                        </div>
                        <div className="text-[11px] text-[#9CA3AF]">{e.point_count} points</div>
                      </div>
                      {e.fault_count > 0 && (
                        <span
                          className="inline-flex items-center gap-1 text-[10px] font-heading font-semibold shrink-0"
                          style={{ color: e.critical_fault_count ? "#B91C1C" : "#C2410C" }}
                          title={`${e.fault_count} open fault(s) · ${e.fault_hours} hr`}
                          data-testid={`equipment-item-faults-${e.equip_id}`}
                        >
                          <AlertOctagon className="h-3 w-3" /> {e.fault_count}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
          {!loading && equips.length === 0 && (
            <div className="k-card p-6 text-sm text-[#6B7280]" data-testid="equipment-empty">
              No equipment records for this site yet. They appear after the next data sync.
            </div>
          )}
        </div>

        {/* Detail */}
        <div className="lg:col-span-8">
          {loadingDetail && !detail ? (
            <div className="k-card p-8 k-sublabel">Loading equipment…</div>
          ) : detail ? (
            <div data-testid="equipment-detail">
              <div className="k-card p-6">
                <div className="flex items-start justify-between flex-wrap gap-3">
                  <div>
                    <div className="k-label">{detail.system}</div>
                    <h2 className="mt-1 text-2xl font-heading font-bold text-[#212121]" data-testid="equipment-detail-title">
                      {detail.dis}
                    </h2>
                  </div>
                  <div className="flex items-center gap-6 text-right">
                    <div>
                      <div className="k-sublabel">Points</div>
                      <div className="text-lg font-heading font-semibold text-[#212121]">{detail.points.length}</div>
                    </div>
                    <div>
                      <div className="k-sublabel">Open faults</div>
                      <div className="text-lg font-heading font-semibold" style={{ color: detail.fault_count ? "#B91C1C" : "#212121" }} data-testid="equipment-fault-count">
                        {detail.fault_count}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <OpenFaults detail={detail} />

              {detail.series.length > 0 ? (
                <div className="mt-6 grid grid-cols-1 xl:grid-cols-2 gap-6">
                  {detail.series.map((s) => (
                    <TrendCard key={s.point_id} s={s} />
                  ))}
                </div>
              ) : loadingCharts ? (
                <div className="mt-6 k-card p-6 k-sublabel" data-testid="equipment-charts-loading">
                  Loading trends…
                </div>
              ) : null}

              <div className="mt-6">
                <h3 className="k-label">Live values</h3>
                <div className="mt-2 k-card overflow-hidden max-h-[520px] overflow-y-auto">
                  {detail.points.length === 0 ? (
                    <div className="p-6 text-sm text-[#6B7280]">No points on this equipment.</div>
                  ) : (
                    detail.points.map((p) => <PointValue key={p.point_id} p={p} />)
                  )}
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
